import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import { accountCenterTwoFactorAuthUrl, homeUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import {
  evaluateXpath,
  getCommonAncestor,
  getNearestElementMatch,
} from '../interruptibleXpath'
import { NeedsUserInputError } from '../needsInputError'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key) => getIsFulfilledFnFromKey(key, (value) => value)

const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

enum StageKey {
  GATHER_SETTING_TWO_FACTOR_AUTHENTICATION = 'GATHER_SETTING_TWO_FACTOR_AUTHENTICATION',
  FINISH_SETTING_TWO_FACTOR_AUTHENTICATION = 'FINISH_SETTING_TWO_FACTOR_AUTHENTICATION',
}

export async function getAccountCenterModal2faAccountRow(
  platformAccountUsername: string,
): Promise<HTMLElement | undefined> {
  const modal = await getWaitForElementToAppearPromise(
    document,
    (node) => evaluateXpath<HTMLElement>(node, '//div[@role="dialog"]')[0],
    5000,
  )

  // This function will generally fail silently if elements are not found.
  // Since this recommendation does not have an automated update, gather failure
  // does not risk subsequent errors for the user.
  if (!modal) {
    return undefined
  }

  const accountNameElement = evaluateXpath<HTMLElement>(
    modal,
    `.//*[contains(text(),"${platformAccountUsername}")]`,
  )[0]

  if (!accountNameElement) {
    return undefined
  }

  const platformLabel = getNearestElementMatch(
    accountNameElement,
    `*[contains(text(), "Facebook")]`,
    'following',
  )

  if (!platformLabel) {
    return undefined
  }

  const row = getCommonAncestor(accountNameElement, platformLabel)

  if (!row) {
    return undefined
  }

  return row
}

async function getTwoFactorAuthenticationSetting(
  platformAccountUsername: string,
) {
  const accountRow = await getAccountCenterModal2faAccountRow(
    platformAccountUsername,
  )

  if (accountRow) {
    await wait(1000)
    accountRow.click()

    const emailCodeText = await getWaitForElementToAppearPromise(
      document,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          '//*[contains(text(), "Check your email")]',
        )[0],
      3000,
    )

    if (emailCodeText) {
      throw new NeedsUserInputError('Enter the emailed code to continue')
    }

    const twofaOnHeaderText = await getWaitForElementToAppearPromise(
      document,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          '//*[text()="Two-factor authentication is on"]',
        )[0],
      5000,
    )

    if (twofaOnHeaderText) {
      return true
    }
  }

  return false
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_TWO_FACTOR_AUTHENTICATION,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: accountCenterTwoFactorAuthUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues, platformAccountUsername } = userContext

      const twofaSetting = await getTwoFactorAuthenticationSetting(
        platformAccountUsername,
      )

      await updateStoredInitialValues(
        {
          [FacebookSettingKey.SETUP_2FA]: twofaSetting,
        },
        enums.FBRecommendationKey.FB_SETUP_2FA,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.FINISH_SETTING_TWO_FACTOR_AUTHENTICATION,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: homeUrl,
    action: async () => true,
  },
]

const updateStageDefs: StageDef[] = []
const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
